// Right-hand HUD rail (cockpit layout). Presentational only — all data is
// passed in, so it is fully unit-testable. Card-grouped workspace dashboard.

import { parseDecimal } from "../utils";

const STATUS = {
  connected: { dot: "#19865d", label: "已連線記錄軟體", bg: "#e4f4eb", fg: "#166043" },
  unmatched: { dot: "#c58b00", label: "未配對記錄軟體", bg: "#fff4cb", fg: "#765500" },
  disconnected: { dot: "#c54a4a", label: "連線中斷", bg: "#ffedeb", fg: "#953536" },
};

const fmt = (n) => (typeof n === "number" ? n.toFixed(1) : "—");

export const HudRail = ({
  status = "disconnected",
  videoTime = "00:00:00",
  count = 0,
  last = null,
  lastStatus = "sent",
  recent = [],
  gameState = null,
  gameStateError = false,
  onRefreshGameState,
  onReconnect,
  x = null,
  y = null,
  startSecond = null,
  endSecond = null,
  pickoffFeedback = null,
}) => {
  const s = STATUS[status] || STATUS.disconnected;
  const S = {
    rail: { gap: 10, padding: 0, boxSizing: "border-box", background: "transparent", color: "var(--sp-ink)", borderRadius: 12 },
    pill: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, padding: "8px 10px", borderRadius: 8, background: s.bg, color: s.fg },
    pickoffBadge: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 600, padding: "6px 10px", borderRadius: 8, background: "var(--sp-blue)", color: "var(--sp-link)" },
    reconnectBtn: { marginLeft: "auto", fontSize: 11, padding: "2px 8px", color: "var(--sp-link)", background: "var(--sp-card)", border: "1px solid var(--sp-line)", borderRadius: 8, cursor: "pointer" },
    dot: { width: 9, height: 9, borderRadius: "50%", background: s.dot, flexShrink: 0 },
    card: { background: "var(--sp-card)", border: "1px solid var(--sp-line)", borderRadius: 10, padding: "14px 16px" },
    label: { fontSize: 11, letterSpacing: 1, color: "var(--sp-muted)", textTransform: "uppercase" },
    vt: { fontSize: 32, fontWeight: 400, fontVariantNumeric: "tabular-nums", lineHeight: 1.1, color: "var(--sp-ink)", marginTop: 6 },
    count: { fontSize: 13, color: "var(--sp-muted)", marginTop: 8 },
    gsHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
    gsRow: { display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 13, padding: "5px 0", borderBottom: "1px solid var(--sp-line)" },
    rowLabel: { color: "var(--sp-muted)" },
    rowVal: { fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "var(--sp-ink)" },
    refreshBtn: { fontSize: 11, padding: "2px 8px", color: "var(--sp-link)", background: "var(--sp-card)", border: "1px solid var(--sp-line)", borderRadius: 8, cursor: "pointer" },
    ok: { color: "#166043", fontWeight: 600 },
    fail: { color: "#9f2f35", fontWeight: 600 },
    muted: { color: "var(--sp-muted)", fontSize: 13 },
    recentItem: { display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", color: "var(--sp-muted)" },
  };

  const gsRow = (label, value) => (
    <div style={S.gsRow}>
      <span style={S.rowLabel}>{label}</span>
      <b style={S.rowVal}>{value ?? "—"}</b>
    </div>
  );

  return (
    <aside className="hud-rail" style={S.rail}>
      <div className="hud-status" style={S.pill}>
        <span className="hud-dot" style={S.dot} /> {s.label}
        {status === "disconnected" && onReconnect && (
          <button type="button" style={S.reconnectBtn} onClick={onReconnect}>重新連線</button>
        )}
      </div>

      {pickoffFeedback && (
        <div className="hud-pickoff-feedback" style={S.pickoffBadge}>已記錄牽制 @ {pickoffFeedback}</div>
      )}

      <div className="card" style={S.card}>
        <div style={S.label}>目前 VideoTime</div>
        <div className="hud-videotime" style={S.vt}>{videoTime}</div>
        <div className="hud-count" style={S.count}>本次連線已送 <b style={{ color: "var(--sp-ink)" }}>{count}</b> 球</div>
      </div>

      <div className="card hud-coordinate" style={S.card}>
        <div style={S.label}>點擊座標／計時器</div>
        <div className="hud-coordinate-xy" style={{ marginTop: 4, color: "var(--sp-ink)" }}>
          點擊座標 x: {x == null ? "NA" : parseDecimal(x - 45)} y: {y == null ? "NA" : parseDecimal(74.22 - y)}
        </div>
        <div className="hud-coordinate-timer" style={{ ...S.muted, marginTop: 4 }}>
          計時器：{startSecond === null ? "未設置開始時間" : (endSecond === null ? "已設置開始時間" : `時間區間 = ${parseDecimal(endSecond - startSecond)}`)}
        </div>
      </div>

      <div className="card hud-gamestate" style={S.card}>
        <div style={S.gsHead}>
          <span style={S.label}>賽事現況</span>
          {onRefreshGameState && (
            <button type="button" style={S.refreshBtn} aria-label="更新現況" onClick={onRefreshGameState}>↻ 更新</button>
          )}
        </div>
        {gameState ? (
          <div>
            {gsRow("局數", `${gameState.inning} 局${gameState.inning_half === "bottom" ? "下" : "上"}`)}
            {gsRow("B-S-O", `${gameState.balls}-${gameState.strikes}-${gameState.outs}`)}
            {gsRow("打者", gameState.batter?.name)}
            {gsRow("投手", gameState.pitcher?.name)}
            {gsRow("投手球數", gameState.pitcher_pitches)}
            {/* fetch failed after a prior success — keep last data but flag it stale */}
            {gameStateError && <div style={{ ...S.fail, fontSize: 11, marginTop: 4 }}>現況無法更新</div>}
          </div>
        ) : (
          <div style={S.muted}>{gameStateError ? "現況無法取得" : "尚未連結賽事"}</div>
        )}
      </div>

      <div className="card" style={S.card}>
        <div style={S.label}>上一球</div>
        {last ? (
          <div className="hud-last-card" style={{ marginTop: 4 }}>
            <div style={lastStatus === "sent" ? S.ok : S.fail}>
              {lastStatus === "sent" ? "✓ 已送出" : "✗ 送出失敗"}
            </div>
            <div style={S.muted}>位置 ({fmt(last.KZoneY)}, {fmt(last.KZoneZ)})</div>
            <div style={S.muted}>VideoTime {last.VideoTime}</div>
          </div>
        ) : (
          <div style={{ ...S.muted, marginTop: 4 }}>尚無</div>
        )}

        <div style={{ ...S.label, marginTop: 12 }}>近期</div>
        {recent.length === 0 && <div style={{ ...S.muted, marginTop: 4 }}>—</div>}
        {recent.map((p, i) => (
          <div key={i} className="hud-recent-item" style={S.recentItem}>
            <span>#{count - i}</span>
            <span>{p.VideoTime}</span>
          </div>
        ))}
      </div>
    </aside>
  );
};
